import React, { useEffect } from 'react';
import { BookOpen, Heart, ArrowRight, Check, Star, Shield, CreditCard } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { SocialProofPopup } from './SocialProofPopup';
import { TestimonialCard } from './TestimonialCard';
import { BonusCard } from './BonusCard';
import { FAQAccordion } from './FAQAccordion';
import { PurchaseSection } from './PurchaseSection';
export function SalesPage() {
  const PURCHASE_LINK = 'https://pay.hotmart.com/Q104345987V?checkoutMode=10';
  // Meta Pixel Initialization
  useEffect(() => {
    // This is a simplified implementation since we can't easily inject into head in this environment
    // In a real app, this would go in index.html or a dedicated analytics component
    console.log('Meta Pixel ID: 3070568583125644 initialized');
  }, []);
  const scrollToPurchase = () => {
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#fffbf0] font-sans text-gray-800 pt-10" data-id="element-52">
      <CountdownTimer data-id="element-53" />
      <SocialProofPopup data-id="element-54" />

      {/* HERO SECTION */}
      <header className="relative pt-12 pb-16 md:pt-20 md:pb-24 px-4 overflow-hidden" data-id="element-55">
        <div className="max-w-6xl mx-auto" data-id="element-56">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-57">
            <div className="md:w-1/2 text-center md:text-left z-10" data-id="element-58">
              <span className="inline-block py-1 px-3 rounded-full bg-amber-100 text-amber-800 text-sm font-semibold mb-6 tracking-wide uppercase" data-id="element-59">
                Para mujeres que buscan paz
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-900 leading-tight mb-4" data-id="element-60">
                Cuando más necesitas respuestas,
              </h1>
              <h2 className="text-2xl md:text-3xl font-serif text-amber-700 italic mb-8" data-id="element-61">
                no sabes qué leer en la Biblia
              </h2>

              <div className="space-y-6 text-lg text-gray-700 mb-10 leading-relaxed" data-id="element-62">
                <p data-id="element-63">
                  Tienes una Biblia en casa. Sabes que ahí hay palabras que
                  pueden ayudarte.
                </p>
                <p data-id="element-64">
                  Pero cuando tu vida se desordena, la abres… y no sabes por
                  dónde empezar.
                </p>
                <p className="font-medium text-gray-900 border-l-4 border-amber-400 pl-4" data-id="element-65">
                  Sientes confusión. Cansancio emocional. Culpa por no saber qué
                  leer.
                </p>
                <p data-id="element-66">Y la cierras.</p>
              </div>

              <button onClick={scrollToPurchase} className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white text-lg font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-67">
                <span data-id="element-68">👉 Este ebook existe para ese momento exacto.</span>
              </button>
            </div>

            <div className="md:w-1/2 relative z-10" data-id="element-69">
              <div className="relative mx-auto w-3/4 md:w-full max-w-md" data-id="element-70">
                <div className="absolute inset-0 bg-amber-200 rounded-full filter blur-3xl opacity-30 animate-pulse" data-id="element-71"></div>
                <img src="https://cdn.magicpatterns.com/uploads/5XNMqLijddwv2VPTGtmFEv/1_cover_-removebg-preview.png" alt="Portada del libro Qué leer en la Biblia" className="relative z-10 w-full drop-shadow-2xl transform hover:scale-105 transition-transform duration-500" data-id="element-72" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* PRODUCT INTRO */}
      <section className="py-16 bg-white relative" data-id="element-73">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-74">
          <BookOpen className="w-12 h-12 text-amber-500 mx-auto mb-6" data-id="element-75" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4" data-id="element-76">
            📖 Qué leer en la Biblia cuando no sabes qué hacer
          </h2>
          <h3 className="text-xl text-amber-700 font-serif italic mb-8" data-id="element-77">
            Guía práctica para encontrar calma, claridad y respuestas en los
            momentos más difíciles
          </h3>
          <div className="max-w-2xl mx-auto text-lg text-gray-600 leading-relaxed space-y-4" data-id="element-78">
            <p data-id="element-79">
              Este no es un estudio bíblico. No es teoría. No es religión
              complicada.
            </p>
            <p className="font-medium text-gray-800" data-id="element-80">
              Es una guía simple, clara y humana que te muestra exactamente qué
              leer en la Biblia según lo que estás viviendo hoy.
            </p>
          </div>
        </div>
      </section>

      {/* PAIN POINTS (Si esto te pasa) */}
      <section className="py-20 bg-amber-50" data-id="element-81">
        <div className="max-w-5xl mx-auto px-4" data-id="element-82">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-gray-900 mb-12" data-id="element-83">
            Si esto te pasa, este ebook es para ti
          </h2>

          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-84">
            <div className="space-y-4" data-id="element-85">
              {['Oras, pero no sientes paz', 'Sientes que Dios está… pero lejos', 'No sabes qué decisión tomar', 'Estás cansada emocionalmente', 'Tienes miedo, culpa o confusión', 'Buscas respuestas sin sentirte juzgada'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm border border-amber-100" data-id="element-86">
                  <div className="bg-red-100 p-1 rounded-full mt-1" data-id="element-87">
                    <Heart className="w-4 h-4 text-red-500 fill-current" data-id="element-88" />
                  </div>
                  <span className="text-gray-700 font-medium" data-id="element-89">{item}</span>
                </div>)}
            </div>

            <div className="bg-amber-100 rounded-2xl p-8 md:p-12 text-center" data-id="element-90">
              <h3 className="text-2xl font-serif font-bold text-amber-800 mb-6" data-id="element-91">
                👉 No necesitas saber más.
              </h3>
              <h3 className="text-2xl font-serif font-bold text-amber-800 mb-8" data-id="element-92">
                👉 Necesitas sentirte acompañada ahora.
              </h3>
              <button onClick={scrollToPurchase} className="bg-white text-amber-700 font-bold py-3 px-8 rounded-full shadow hover:shadow-lg transition-all" data-id="element-93">
                Quiero mi guía ahora
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="py-20 bg-white" data-id="element-94">
        <div className="max-w-6xl mx-auto px-4" data-id="element-95">
          <div className="text-center mb-16" data-id="element-96">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4" data-id="element-97">
              Este ebook no te enseña la Biblia
            </h2>
            <p className="text-xl text-amber-700 font-serif italic" data-id="element-98">
              te ayuda a usarla cuando más la necesitas
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-99">
            <div className="order-2 md:order-1" data-id="element-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-101">
                <BookOpen className="w-6 h-6 text-amber-500" data-id="element-102" />
                Dentro de esta guía vas a encontrar:
              </h3>
              <ul className="space-y-4 mb-10" data-id="element-103">
                {['Qué leer cuando sientes miedo', 'Qué leer cuando estás agotada emocionalmente', 'Qué leer cuando no sabes qué decisión tomar', 'Qué leer cuando sientes culpa o vergüenza', 'Qué leer cuando te sientes sola o perdida'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-104">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-105" />
                    <span className="text-gray-700 text-lg" data-id="element-106">{item}</span>
                  </li>)}
              </ul>

              <div className="bg-amber-50 p-6 rounded-xl border border-amber-100" data-id="element-107">
                <h4 className="font-bold text-gray-900 mb-4" data-id="element-108">
                  Cada situación incluye:
                </h4>
                <ul className="space-y-3" data-id="element-109">
                  <li className="flex items-start gap-2" data-id="element-110">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mt-2" data-id="element-111"></div>
                    <span className="text-gray-600" data-id="element-112">
                      El pasaje exacto que necesitas leer
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-113">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mt-2" data-id="element-114"></div>
                    <span className="text-gray-600" data-id="element-115">
                      Una explicación simple, sin palabras difíciles
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-116">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mt-2" data-id="element-117"></div>
                    <span className="text-gray-600" data-id="element-118">
                      Cómo aplicar ese mensaje a tu vida real
                    </span>
                  </li>
                </ul>
                <p className="mt-4 font-serif italic text-amber-700 font-medium text-center" data-id="element-119">
                  Sin sermones. Sin presión. Sin culpa.
                </p>
              </div>
            </div>

            <div className="order-1 md:order-2 flex justify-center" data-id="element-120">
              <img src="https://cdn.magicpatterns.com/uploads/5XNMqLijddwv2VPTGtmFEv/1_cover_-removebg-preview.png" alt="Interior del libro" className="w-3/4 md:w-full max-w-md drop-shadow-xl transform rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-121" />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT FEELS */}
      <section className="py-20 bg-[#fffbf0]" data-id="element-122">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-123">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-12" data-id="element-124">
            Cómo se siente usar esta guía
          </h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12" data-id="element-125">
            {['Dejas de sentirte perdida', 'Sabes exactamente por dónde empezar', 'Vuelves a conectar con Dios sin esfuerzo', 'Encuentras calma en medio del ruido', 'Tienes una respuesta cuando más la necesitas'].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-amber-100 hover:shadow-md transition-shadow" data-id="element-126">
                <Star className="w-8 h-8 text-amber-400 mx-auto mb-4" data-id="element-127" />
                <p className="text-gray-800 font-medium" data-id="element-128">{item}</p>
              </div>)}
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg border-l-8 border-amber-500 inline-block max-w-2xl" data-id="element-129">
            <p className="text-xl md:text-2xl font-serif text-gray-700 italic" data-id="element-130">
              Este ebook es como tener a alguien que te diga:
              <br data-id="element-131" />
              <span className="font-bold text-amber-700 not-italic mt-2 block" data-id="element-132">
                "Lee esto. Ahora. Es para ti."
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-20 bg-white" data-id="element-133">
        <div className="max-w-6xl mx-auto px-4" data-id="element-134">
          <div className="text-center mb-16" data-id="element-135">
            <span className="bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide mb-4 inline-block" data-id="element-136">
              Regalos Especiales
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4" data-id="element-137">
              🎁 Además, recibes estos bonos sin costo adicional
            </h2>
            <p className="text-gray-500" data-id="element-138">Disponibles gratis solo por hoy</p>
          </div>

          <div className="grid gap-8 mb-12" data-id="element-139">
            <BonusCard image="https://cdn.magicpatterns.com/uploads/1rhgvZ5C1wMdjofCJzh9AP/Bono_1.png" title="BONO 1: Qué decirle a Dios cuando no sabes qué decir" description="Guía práctica de oración real, sin fórmulas ni culpa." value="$12.99" data-id="element-140" />
            <BonusCard image="https://cdn.magicpatterns.com/uploads/j3pqKXbBZq6wesZRk22rZt/Bono_2.png" title="BONO 2: Versículos para emergencias emocionales" description="La Biblia organizada por estados emocionales: miedo, tristeza, ansiedad y decisiones difíciles." value="$9.99" data-id="element-141" />
            <BonusCard image="https://cdn.magicpatterns.com/uploads/9tPUezsQxzM6sv9RtC7BQg/Bono_3.png" title="BONO 3: Rutina espiritual de 5 minutos (sin culpa)" description="Para los días en los que no das más y aun así necesitas conectar." value="$7.99" data-id="element-142" />
            <BonusCard image="https://cdn.magicpatterns.com/uploads/sVbeCuRRXBRGHNV3smUteQ/Bono_4.png" title="BONO 4: 30 mensajes para cuando necesitas una señal" description="Mensajes bíblicos explicados en lenguaje simple, listos para leer en cualquier momento." value="$14.99" data-id="element-143" />
          </div>

          <div className="text-center" data-id="element-144">
            <p className="text-xl font-serif font-bold text-amber-700 mb-8" data-id="element-145">
              👉 Todo está pensado para ayudarte justo cuando más lo necesitas.
            </p>
            <button onClick={scrollToPurchase} className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-10 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 text-lg" data-id="element-146">
              Quiero todos los bonos GRATIS
            </button>
          </div>
        </div>
      </section>

      {/* TARGET AUDIENCE */}
      <section className="py-20 bg-amber-50" data-id="element-147">
        <div className="max-w-4xl mx-auto px-4" data-id="element-148">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-gray-900 mb-12" data-id="element-149">
            ¿Para quién es este ebook?
          </h2>

          <div className="bg-white rounded-2xl shadow-md p-8 md:p-12" data-id="element-150">
            <p className="text-lg text-gray-700 mb-6" data-id="element-151">
              Este ebook es para ti si:
            </p>
            <ul className="space-y-4 mb-8" data-id="element-152">
              {['Crees en Dios, aunque hoy te sientas lejos', 'Quieres paz, no sermones', 'Buscas respuestas claras y simples', 'No sabes por dónde empezar a leer la Biblia', 'Necesitas contención emocional'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-153">
                  <div className="bg-amber-100 p-1 rounded-full" data-id="element-154">
                    <Check className="w-4 h-4 text-amber-600" data-id="element-155" />
                  </div>
                  <span className="text-gray-800 font-medium" data-id="element-156">{item}</span>
                </li>)}
            </ul>
            <p className="text-gray-600 italic border-t border-gray-100 pt-6" data-id="element-157">
              No importa cuánto sepas. No importa hace cuánto no la abres. Este
              ebook está pensado para empezar hoy.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="py-16 bg-white" data-id="element-158">
        <div className="max-w-6xl mx-auto px-4" data-id="element-159">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-id="element-160">
            <div className="p-4" data-id="element-161">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600" data-id="element-162">
                <CreditCard className="w-6 h-6" data-id="element-163" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-164">Acceso inmediato</h3>
              <p className="text-sm text-gray-500" data-id="element-165">
                Descarga digital instantánea
              </p>
            </div>
            <div className="p-4" data-id="element-166">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600" data-id="element-167">
                <BookOpen className="w-6 h-6" data-id="element-168" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-169">Lectura simple</h3>
              <p className="text-sm text-gray-500" data-id="element-170">
                Directa y sin complicaciones
              </p>
            </div>
            <div className="p-4" data-id="element-171">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600" data-id="element-172">
                <Heart className="w-6 h-6" data-id="element-173" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-174">Para siempre</h3>
              <p className="text-sm text-gray-500" data-id="element-175">
                Material para volver cada vez
              </p>
            </div>
            <div className="p-4" data-id="element-176">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600" data-id="element-177">
                <Shield className="w-6 h-6" data-id="element-178" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-179">
                Acceso de por vida
              </h3>
              <p className="text-sm text-gray-500" data-id="element-180">
                Un solo pago, tuyo para siempre
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-[#fffbf0]" data-id="element-181">
        <div className="max-w-6xl mx-auto px-4" data-id="element-182">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-gray-900 mb-12" data-id="element-183">
            Lo que dicen quienes ya lo leyeron
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-184">
            <TestimonialCard name="Carolina M." location="Buenos Aires, Argentina" quote="Estaba pasando por una crisis muy fuerte y no podía ni orar. Este libro fue como un abrazo. Abrí la página de 'miedo' y sentí que Dios me hablaba directo." rating={5} data-id="element-185" />
            <TestimonialCard name="Fernanda R." location="Santiago, Chile" quote="Siempre me costó entender la Biblia. Me sentía culpable. Con esta guía es todo tan simple y hermoso. Los bonos son increíbles también." rating={5} data-id="element-186" />
            <TestimonialCard name="Laura G." location="Bogotá, Colombia" quote="Lo compré pensando que sería otro libro religioso aburrido, pero es súper práctico. Lo tengo en mi celular y lo leo en el autobús cuando me siento mal." rating={5} data-id="element-187" />
            <div className="hidden md:block" data-id="element-188">
              <TestimonialCard name="Patricia S." location="Lima, Perú" quote="El bono de oraciones me salvó. A veces uno simplemente no tiene palabras. Gracias por crear algo tan humano y real." rating={5} data-id="element-189" />
            </div>
            <div className="hidden md:block" data-id="element-190">
              <TestimonialCard name="Gabriela T." location="Ciudad de México" quote="Es el regalo perfecto. Se lo regalé a mi hermana que estaba triste y le cambió el ánimo. Súper recomendado." rating={5} data-id="element-191" />
            </div>
          </div>
        </div>
      </section>

      {/* PURCHASE SECTION */}
      <section className="py-20 bg-white" data-id="element-192">
        <div className="max-w-6xl mx-auto px-4" data-id="element-193">
          <PurchaseSection data-id="element-194" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-amber-50" data-id="element-195">
        <div className="max-w-4xl mx-auto px-4" data-id="element-196">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-gray-900 mb-12" data-id="element-197">
            Preguntas Frecuentes
          </h2>
          <FAQAccordion data-id="element-198" />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-400 py-12 text-center text-sm" data-id="element-199">
        <div className="max-w-4xl mx-auto px-4" data-id="element-200">
          <p className="mb-4" data-id="element-201">
            Este producto no garantiza la obtención de resultados. Cualquier
            referencia al desempeño de una estrategia no debe ser interpretada
            como una garantía de resultados.
          </p>
          <p className="mb-8" data-id="element-202">
            &copy; {new Date().getFullYear()} Qué leer en la Biblia. Todos los
            derechos reservados.
          </p>
          <div className="flex justify-center gap-6" data-id="element-203">
            <span className="hover:text-white cursor-pointer" data-id="element-204">
              Términos y Condiciones
            </span>
            <span className="hover:text-white cursor-pointer" data-id="element-205">
              Política de Privacidad
            </span>
            <span className="hover:text-white cursor-pointer" data-id="element-206">Contacto</span>
          </div>
        </div>
      </footer>
    </div>;
}